import { useEffect } from 'react';
import { $generateNodesFromDOM } from '@lexical/html';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createParagraphNode, $getRoot, CLEAR_HISTORY_COMMAND } from 'lexical';
import { $createHtmlNode } from '../nodes/html/HtmlNode';

export default function InitialEditorStatePlugin({ initialValue }) {
  const [editor] = useLexicalComposerContext();

  const updateHTML = (editor, value, clear) => {
    const root = $getRoot();
    const parser = new DOMParser();
    const dom = parser.parseFromString(value, 'text/html');

    const containsHtml = Array.from(dom.body.childNodes).some(
      node => node.nodeType === 1,
    );

    const isWrappedInHtmlBlock = dom.body.childNodes.length === 1;

    if (containsHtml) {
      let htmlText = initialValue;

      if (!isWrappedInHtmlBlock) {
        const placeholder = 'PLACEHOLDER_FOR_DOUBLE_BR'; // Choose a unique placeholder

        htmlText = initialValue
          .replace(/\n\n/g, placeholder) // Replace double line breaks with placeholder
          .replace(/\n/g, '\n<br>\n') // Replace remaining single line breaks with <br>
          .replace(new RegExp(placeholder, 'g'), '\n<br>\n<br>\n'); // Replace placeholders with \n<br><br>\n
      }

      const parser = new DOMParser();
      const dom = parser.parseFromString(htmlText, 'text/html');

      // wrap the entire value in a custom html block
      const htmlNode = $createHtmlNode(dom.body);

      htmlNode.__html = dom.body.innerHTML;

      // append the html node to the root
      root.append(htmlNode);

      // remove the first child, which is the empty paragraph
      root.getFirstChild().remove();

      editor.dispatchCommand(CLEAR_HISTORY_COMMAND, void 0);
      return;
    } else {
      // replace double linebreaks with paragraph tags
      let htmlText = initialValue.replace(/\n\n/g, '<p></p>');

      // replace single linebreaks with linebreak tags
      htmlText = htmlText.replace(/\n/g, '<br>');

      const parser = new DOMParser();
      const dom = parser.parseFromString(htmlText, 'text/html');

      const nodes = $generateNodesFromDOM(editor, dom);

      let currentParagraph = $createParagraphNode();
      const finalNodes = [];

      nodes.forEach((node, i, array) => {
        // Check if the node is a text or link type
        if (node.getType() === 'text' || node.getType() === 'link') {
          currentParagraph.append(node);
        } else {
          // Handle other types of nodes that may require starting a new paragraph
          if (currentParagraph.getChildren().length > 0) {
            finalNodes.push(currentParagraph);
            currentParagraph = $createParagraphNode();
          }

          if (node.getType() !== 'linebreak') {
            // If the node is not a linebreak, add it as a separate entity
            finalNodes.push(node);
          }
        }
      });

      // Check if the last paragraph has children and add it if it does
      if (currentParagraph.getChildren().length > 0) {
        finalNodes.push(currentParagraph);
      }

      if (clear) {
        root.clear();
      }

      root.append(...finalNodes);

      editor.dispatchCommand(CLEAR_HISTORY_COMMAND, void 0);
    }
  };

  useEffect(() => {
    if (!editor) return;

    if (!initialValue) return;

    try {
      // if previously saved via Lexical, the initial value will be valid JSON
      const isValidJSON = JSON.parse(initialValue);

      if (!isValidJSON) {
        throw new Error('Invalid JSON');
      }

      // initial value is already set as the editor state, so we don't need to update it
    } catch (error) {
      // if previously saved via the old editor, the initial value will be plain text with HTML tags

      // determine whether the initialValue contains any HTML tags
      // in that case, we will wrap the entire value in a custom html block
      // otherwise, we will wrap the entire value in a paragraph block

      editor.update(() => {
        updateHTML(editor, initialValue, true);
      });
    }
  }, []);

  return null;
}
